import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Select, message, App } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import supplyChainService, { Supplier } from '../../../services/supplyChainService';
import { FormPageLayout } from '../../../components/FormPageLayout';
import { useAutoSave } from '../../../hooks/useAutoSave';

const { Option } = Select;

const SupplierEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<Supplier>({
        onSave: async (data) => {
            if (isNew) {
                const result = await supplyChainService.createSupplier(data);
                message.success('供应商已创建');
                // Redirect to edit page with new ID
                navigate(`/supply-chain/procurement/suppliers/${result.data.id}`);
            } else {
                await supplyChainService.updateSupplier(parseInt(id), data);
            }
        },
        delay: 1000,
        enabled: !isNew, // Only auto-save for existing suppliers
        showMessage: true
    });

    useEffect(() => {
        if (!isNew) {
            fetchSupplier();
        } else {
            // Set default values for new supplier
            form.setFieldsValue({
                status: 'active',
                lead_time_days: 15
            });
        }
    }, [id]);

    const fetchSupplier = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getSuppliers({ page_size: 1000 });
            const suppliers = (res.data as any).results || res.data || [];
            const supplier = suppliers.find((s: Supplier) => s.id === parseInt(id!));

            if (supplier) {
                form.setFieldsValue(supplier);
            } else {
                message.error('供应商不存在');
                navigate('/supply-chain/procurement/suppliers');
            }
        } catch (error) {
            message.error('加载供应商信息失败');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: Supplier) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            if (isNew) {
                const result = await supplyChainService.createSupplier(values);
                message.success('供应商已创建');
                navigate(`/supply-chain/procurement/suppliers/${result.data.id}`);
            }
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '新增供应商' : '编辑供应商'}
            breadcrumbs={[
                { title: '供应链', path: '/supply-chain' },
                { title: '采购管理', path: '/supply-chain/procurement' },
                { title: '供应商列表', path: '/supply-chain/procurement/suppliers' },
                { title: isNew ? '新增' : '编辑' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
            >
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ marginBottom: 16 }}>基本信息</h3>
                    <Form.Item
                        name="name"
                        label="供应商名称"
                        rules={[{ required: true, message: '请输入供应商名称' }]}
                    >
                        <Input placeholder="请输入供应商名称" />
                    </Form.Item>

                    <Form.Item name="code" label="供应商代码">
                        <Input placeholder="例如: SUP001" />
                    </Form.Item>

                    <Form.Item name="contact_name" label="联系人">
                        <Input placeholder="请输入联系人姓名" />
                    </Form.Item>

                    <Form.Item name="phone" label="电话">
                        <Input placeholder="请输入联系电话" />
                    </Form.Item>

                    <Form.Item name="email" label="邮箱">
                        <Input placeholder="请输入邮箱" />
                    </Form.Item>

                    <Form.Item name="address" label="地址">
                        <Input.TextArea rows={2} placeholder="请输入地址" />
                    </Form.Item>

                    <Form.Item name="payment_terms" label="结算方式">
                        <Input placeholder="例如: 月结30天, 预付30%" />
                    </Form.Item>

                    <Form.Item name="lead_time_days" label="采购提前期(天)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="link" label="主页链接">
                        <Input placeholder="1688/阿里巴巴链接" />
                    </Form.Item>

                    <Form.Item name="status" label="状态">
                        <Select>
                            <Option value="active">启用</Option>
                            <Option value="inactive">停用</Option>
                        </Select>
                    </Form.Item>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ marginBottom: 16 }}>工厂资质</h3>
                    <Form.Item name="is_factory_audited" valuePropName="checked">
                        <Input.Group compact>
                            <span style={{ display: 'inline-block', marginRight: 8 }}>
                                <input type="checkbox" onChange={(e) => form.setFieldValue('is_factory_audited', e.target.checked)} />
                                是否验厂
                            </span>
                        </Input.Group>
                    </Form.Item>

                    <Form.Item name="factory_audit_date" label="验厂日期">
                        <Input type="date" />
                    </Form.Item>

                    <Form.Item name="factory_audit_score" label="验厂评分 (0-100)">
                        <InputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="audit_grade" label="验厂等级">
                        <Select placeholder="选择等级">
                            <Option value="A">A级</Option>
                            <Option value="B">B级</Option>
                            <Option value="C">C级</Option>
                            <Option value="D">D级</Option>
                        </Select>
                    </Form.Item>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ marginBottom: 16 }}>公司信息</h3>
                    <Form.Item name="business_license" label="营业执照号">
                        <Input placeholder="统一社会信用代码" />
                    </Form.Item>

                    <Form.Item name="registered_capital" label="注册资本 (元)">
                        <InputNumber min={0} style={{ width: '100%' }} formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
                    </Form.Item>

                    <Form.Item name="established_year" label="成立年份">
                        <InputNumber min={1900} max={new Date().getFullYear()} style={{ width: '100%' }} />
                    </Form.Item>
                </div>
            </Form>
        </FormPageLayout>
    );
};

export default SupplierEdit;
